import { useState } from 'react';
import { WardMapMulti, isInsideWard, type MapPin, type PinShape } from './WardMap';
import type { IncidentSite } from '../api';

/**
 * Every site with coordinates, plotted and colour+shape coded by status.
 *
 * Shape carries the status as much as colour does: the packet prints on a mono
 * laser, and red/green is exactly the pair a large minority of readers cannot
 * separate. A legend spells both out.
 */
const SHAPE_FOR: Record<string, PinShape> = {
  all_accounted:  'filled',
  partial:        'hollow',
  none_accounted: 'square',
  missing:        'triangle',
  no_occupants:   'hollow',
};

const TONE_FOR: Record<string, string> = {
  all_accounted:  'text-emerald-600 dark:text-emerald-400',
  partial:        'text-amber-600 dark:text-amber-400',
  none_accounted: 'text-rose-600 dark:text-rose-400',
  missing:        'text-rose-700 dark:text-rose-300',
  no_occupants:   'text-slate-400 dark:text-slate-500',
};

const LEGEND: { shape: PinShape; label: string; tone: string }[] = [
  { shape: 'filled',   label: 'All accounted',  tone: TONE_FOR.all_accounted },
  { shape: 'hollow',   label: 'Partial',        tone: TONE_FOR.partial },
  { shape: 'square',   label: 'None accounted', tone: TONE_FOR.none_accounted },
  { shape: 'triangle', label: 'Missing person', tone: TONE_FOR.missing },
];

export function sitePins(sites: IncidentSite[], withLabels = false): MapPin[] {
  return sites
    .filter((s): s is IncidentSite & { lat: number; lng: number } =>
      !s.excluded && s.lat != null && s.lng != null)
    .map((s, i) => ({
      id: s.id,
      lat: s.lat,
      lng: s.lng,
      shape: SHAPE_FOR[s.accountability] ?? 'hollow',
      tone: TONE_FOR[s.accountability] ?? TONE_FOR.no_occupants,
      label: withLabels ? String(i + 1) : undefined,
    }));
}

export default function BoardMap({ sites, scale = 1, onPlace }: {
  sites: IncidentSite[];
  scale?: number;
  onPlace?: (coords: { lat: number; lng: number }) => void;
}) {
  const [offMap, setOffMap] = useState(0);
  const pins = sitePins(sites);
  const missingCoords = sites.length - pins.length;
  // A household's sector says whether it is in the ward; its pin may be a grid
  // point on a boundary street's centreline. Only a site with no household
  // falls back to the pin.
  const inBoundary = new Map(sites.map((s) => [s.id, s.in_boundary]));
  const outsideWard = pins
    .filter((p) => !(inBoundary.get(p.id) ?? isInsideWard(p.lat, p.lng))).length;

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Sector map</span>
        <span className="text-xs tabular-nums text-slate-400 dark:text-slate-500">
          {pins.length - offMap} plotted
        </span>
      </div>

      <div className="px-2 pt-2">
        <WardMapMulti pins={pins} scale={scale} onPlace={onPlace} onOffMap={setOffMap} />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs">
        {LEGEND.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
            <LegendGlyph shape={l.shape} tone={l.tone} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Never let a household vanish from the map without saying so — an
          operator reads absence as "already handled". */}
      {(missingCoords > 0 || offMap > 0 || outsideWard > 0) && (
        <p className="border-t border-slate-100 dark:border-slate-800 px-3 py-1 text-xs text-amber-700 dark:text-amber-400">
          {[
            missingCoords > 0 && `${missingCoords} without coordinates`,
            offMap > 0 && `${offMap} beyond this map`,
            outsideWard > 0 && `${outsideWard} outside the ward boundary`,
          ].filter(Boolean).join(' · ')} — not shown
        </p>
      )}
    </div>
  );
}

function LegendGlyph({ shape, tone }: { shape: PinShape; tone: string }) {
  return (
    <svg viewBox="0 0 12 12" className={`h-3 w-3 ${tone}`} aria-hidden="true">
      {shape === 'filled' && <circle cx="6" cy="6" r="4.5" fill="currentColor" />}
      {shape === 'hollow' && <circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {shape === 'square' && <rect x="1.5" y="1.5" width="9" height="9" fill="currentColor" />}
      {shape === 'triangle' && <path d="M6 1 L11 10.5 L1 10.5 Z" fill="currentColor" />}
    </svg>
  );
}
